/**
 * Shared hls.js tuning for every HLS surface in the app.
 *
 * Extracted from VideoPlayer.tsx so the recording route can use the same
 * numbers without copying them. The two players have not merged yet (ticket
 * 86d40p593 tracks that); this stops them drifting in the meantime.
 *
 * Deliberately does NOT include a `loader`. That is the one setting the two
 * surfaces must differ on:
 *
 *   - R2-backed HLS may use CacheBustLoader, which stamps every request so a
 *     poisoned cache can be bypassed.
 *   - Cloudflare Stream must NOT. That loader makes every request unique by
 *     design, which against Stream forces a cache miss on every segment for
 *     every viewer — the opposite of what we pay Cloudflare for. It is a cost
 *     and stalling problem, not a security one: the signed pass lives in the
 *     URL path, so nothing appended after it can invalidate the signature.
 */

export const BASE_HLS_CONFIG = {
  // Look 30s ahead, cap at 90s. The previous values (600s / 3600s) made hls.js
  // eagerly fetch the entire video before signalling canplay, which delayed
  // startup and left no eviction room — segments got dropped and re-fetched
  // mid-playback.
  maxBufferLength: 30,
  maxMaxBufferLength: 90,
  maxBufferSize: 60 * 1024 * 1024,
  backBufferLength: 30,

  // ── Adaptive quality: automatic, and biased toward starting fast ──────────
  //
  // The brief (15 Sep) is that the player lands on auto and is tuned for speed
  // rather than picture quality. Every value below trades a rung of resolution
  // for a faster first frame or a lower chance of a stall. None of them pins a
  // quality — the viewer still gets whatever their connection sustains, and the
  // settings menu still lets them force a rung.
  //
  // `startLevel: -1` with `testBandwidth` is the mechanism that does most of
  // this. hls.js fetches a fragment of the LOWEST rung first, measures the real
  // download, and only then picks an auto level. So playback starts from the
  // smallest segment on the ladder — 240p on Cloudflare Stream — and climbs,
  // instead of guessing high and stalling. Both are needed: testBandwidth does
  // nothing unless startLevel is -1, in which case hls.js just plays the first
  // level listed in the manifest.
  startLevel: -1,
  testBandwidth: true,

  // Never fetch a rung larger than the element it is painted into. A lesson
  // player in a column does not need 1080p.
  capLevelToPlayerSize: true,

  // Default 4s. This is the buffer starvation ABR is allowed to forecast before
  // it drops a rung. Halving it makes the player give up resolution sooner
  // rather than risk the viewer watching a spinner.
  maxStarvationDelay: 2,

  // Default 0.7. The measured bandwidth must exceed the next rung's bitrate by
  // this factor before ABR climbs. Lowering it means more headroom is demanded
  // before going up, so the player sits at a safe rung instead of oscillating.
  abrBandWidthUpFactor: 0.5,

  // Default 0.95. Scale applied to measured bandwidth when deciding whether the
  // current rung is still sustainable. Slightly under the default, so a
  // connection that is degrading is treated as degraded a little sooner.
  abrBandWidthFactor: 0.9,

  // Default 3.0. The fast half of the bandwidth estimator, in seconds of
  // sampling. Shorter reacts quicker to a connection dropping, which is the
  // whole point of biasing toward speed.
  abrEwmaFastVoD: 2.0,
} as const;
